import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { makePermissionsComplete } from 'server/helpers/permissions/permissions';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { CustomerTypeEnum } from 'server/shared/models/customer';
import { DispoPermission } from 'server/shared/models/dispoPermissions';
import { RoleEnum } from 'server/shared/models/role.enum';
import { SidenavCountTypeEnum } from 'server/shared/models/sidenavCountTypeEnum';
import { SidenavCounts } from 'src/client-collections/sidenav-counts';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { UserService } from '../../services/user.service';
import { SidenavService } from '../sidenav.service';
import { SubscriptionService } from '../subscription.service';

@Component({
  selector: 'sidenav-content',
  templateUrl: 'sidenav-content.component.html',
  styleUrls: ['sidenav-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavContentComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  user$ = this.userService.readyUser$;
  isLoadingRole$ = new BehaviorSubject<boolean>(true);
  isInSetup$ = new BehaviorSubject<boolean>(false);
  navigationSubscription?: any;
  activeRoute$ = new BehaviorSubject<string>('');
  customerHasEmployments$ = new BehaviorSubject<boolean>(false);
  featurePermissions?: DispoPermission['featurePermissions'];

  adminNavigation$ = new BehaviorSubject<boolean>(false);
  superAdminNavigation$ = new BehaviorSubject<boolean>(false);

  displayAdminMenu$ = this.adminNavigation$;
  displaySuperAdminMenu$ = this.superAdminNavigation$;
  displayMainMenu$ = combineLatest([
    this.adminNavigation$,
    this.superAdminNavigation$,
  ]).pipe(map(([adminNav, superAdminNav]) => !adminNav && !superAdminNav));

  roleEnum = RoleEnum;
  customerTypeEnum = CustomerTypeEnum;

  hasExternalAgencyConnections$ = new BehaviorSubject<boolean>(false);
  customerIsSharingJobPool$ = this.customerService.customerIsSharingJobPool$;

  actionRequiredUsersCount$ =
    // because emission is delayed, we need to wait until it is ready
    this.subscriptionService.actionRequiredUsersCount.ready$.pipe(
      switchMap(() => {
        return observeTracker(() => {
          return SidenavCounts.findOne({
            _id: SidenavCountTypeEnum.ACTION_REQUIRED_USERS,
          });
        });
      }),
      map((result) => result?.count || 0),
      rxNgZoneRun(this.ngZone),
    );

  actionRequiredEmploymentsCount$ =
    this.subscriptionService.actionRequiredEmploymentsCount.ready$.pipe(
      switchMap(() => {
        return observeTracker(() => {
          return SidenavCounts.findOne({
            _id: SidenavCountTypeEnum.ACTION_REQUIRED_EMPLOYMENTS,
          });
        });
      }),
      map((result) => result?.count || 0),
      rxNgZoneRun(this.ngZone),
    );

  actionRequiredJobsCount$ =
    this.subscriptionService.actionRequiredJobsCount.ready$.pipe(
      switchMap(() => {
        return observeTracker(() => {
          return SidenavCounts.findOne({
            _id: SidenavCountTypeEnum.ACTION_REQUIRED_JOBS,
          });
        });
      }),
      map((result) => result?.count || 0),
      rxNgZoneRun(this.ngZone),
    );

  jobRequestsCount$ = this.subscriptionService.jobRequestsCount.ready$.pipe(
    switchMap(() => {
      return observeTracker(() => {
        return SidenavCounts.findOne({
          _id: SidenavCountTypeEnum.JOB_REQUESTS,
        });
      });
    }),
    map((result) => result?.count || 0),
    rxNgZoneRun(this.ngZone),
  );

  dispoCustomer$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => {
      this.customerHasEmployments$.next(!!customer?.enableEmployments);
      let sharedPoolCustomers = Customers.find({
        $or: [
          {
            'pool.jobsSharedWithCustomerIds': customer._id,
          },
          {
            'pool.employmentsSharedWithCustomerIds': customer._id,
          },
        ],
      }).cursor.count();

      this.hasExternalAgencyConnections$.next(
        !!(
          customer.customerTransfers?.transfers?.length ||
          customer.customerTransfers?.employmentTransfers?.length ||
          sharedPoolCustomers
        ),
      );
      return customer;
    }),
    shareReplay(),
  );

  customerHasJobs$ = this.dispoCustomer$.pipe(
    map((customer) => customer?.enableJobs !== false),
  );

  hasAdminPermissions$: Observable<Boolean> = this.userService.readyUser$.pipe(
    map((user) => {
      if (user.role == RoleEnum.DISPO) {
        let completePermissions = makePermissionsComplete(
          user.dispoPermissions,
        );
        return !!completePermissions?.dispoAccess.featurePermissions
          .isCustomerAdmin;
      } else {
        return false;
      }
    }),
  );

  hasSuperAdminPermissions$: Observable<Boolean> =
    this.userService.readyUser$.pipe(
      map((user) => user.role === RoleEnum.DISPO && !!user.isAdmin),
    );

  hasJobRequestPermission$: Observable<Boolean> =
    this.userService.readyUser$.pipe(
      map((user) => {
        if (user.role === RoleEnum.REQUESTER) {
          return true;
        } else if (user.role == RoleEnum.DISPO) {
          let completePermissions = makePermissionsComplete(
            user.dispoPermissions,
          );
          return !!completePermissions?.dispoAccess.featurePermissions
            .hasRequestsAccess;
        } else {
          return false;
        }
      }),
    );

  poolerHasJobs$: Observable<Boolean> = this.userService.readyUser$.pipe(
    map((user) => {
      return user.role === RoleEnum.POOL && !!user.hasJobAssignments;
    }),
  );

  poolerHasEmployments$: Observable<Boolean> = this.userService.readyUser$.pipe(
    map((user) => {
      return user.role === RoleEnum.POOL && !!user.hasEmployments;
    }),
  );

  poolerJobsDisabled$: Observable<Boolean> = combineLatest([
    this.userService.readyUser$,
    this.customerService.readyPoolerCustomers$,
  ]).pipe(
    map(([user, customers]) => {
      if (user.role !== RoleEnum.POOL) {
        return false;
      }
      if (!customers || customers.length === 0) {
        return false;
      }
      // Jobs are disabled if ALL customers have enableJobs === false
      return customers.every((customer) => {
        const enableJobs = user.customerInformation?.[customer._id]?.enableJobs;
        return enableJobs === false;
      });
    }),
  );

  poolerEmploymentsDisabled$: Observable<Boolean> = combineLatest([
    this.userService.readyUser$,
    this.customerService.readyPoolerCustomers$,
  ]).pipe(
    map(([user, customers]) => {
      if (user.role !== RoleEnum.POOL) {
        return false;
      }
      if (!customers || customers.length === 0) {
        return false;
      }
      // Employments are disabled if ALL customers have enableEmployments === false
      return customers.every((customer) => {
        const enableEmployments =
          user.customerInformation?.[customer._id]?.enableEmployments;
        return enableEmployments === false;
      });
    }),
  );

  showJobAssignments$: Observable<Boolean> = this.userService.readyUser$.pipe(
    map((user) => {
      if (user.role == RoleEnum.DISPO) {
        let completePermissions = makePermissionsComplete(
          user.dispoPermissions,
        );
        return !!completePermissions?.dispoAccess.featurePermissions
          .hasJobAccess;
      } else if (user.role == RoleEnum.POOL) {
        return !!user.hasJobAssignments;
      } else {
        return false;
      }
    }),
  );

  showEmployment$: Observable<Boolean> = this.userService.readyUser$.pipe(
    map((user) => {
      if (user.role == RoleEnum.DISPO) {
        let completePermissions = makePermissionsComplete(
          user.dispoPermissions,
        );
        return !!completePermissions?.dispoAccess.featurePermissions
          .hasEmploymentAccess;
      } else if (user.role == RoleEnum.POOL) {
        return !!user.hasEmployments;
      } else {
        return false;
      }
    }),
  );

  showTranscribeJobList$: Observable<boolean> =
    this.userService.readyUser$.pipe(
      map((user) => {
        if (user?.role === RoleEnum.DISPO) {
          const completePermissions = makePermissionsComplete(
            user.dispoPermissions,
          );
          return !!completePermissions?.dispoAccess.featurePermissions
            .manageTranscribeJobListPermissions?.read;
        }
        return false;
      }),
    );

  hasCustomerSelect$ = combineLatest([
    this.userService.readyUser$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([user, customer]) => {
      if (user.role === RoleEnum.DISPO) {
        let possibleCustomers = Customers.find({ dispoIds: user._id }).fetch();
        return possibleCustomers.length > 1 || (user.isAdmin && user.isIntemp);
      } else {
        return false;
      }
    }),
    shareReplay(1),
  );

  private userTracker?: Tracker.Computation;

  constructor(
    private location: Location,
    private sidenavService: SidenavService,
    private ngZone: NgZone,
    private router: Router,
    private userService: UserService,
    private customerService: CustomerService,
    public permissionsService: PermissionsService,
    private subscriptionService: SubscriptionService,
  ) {
    // fixes expression has changed after check error, because the template initialized a subscription which set a variable in the component
    this.dispoCustomer$.pipe(takeUntil(this.destroyed$)).subscribe();
  }

  ngOnInit(): void {
    this.userTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        const user = Users.findOne(Meteor.userId());
        if (!user) {
          return;
        }
        this.isInSetup$.next(!!user.isInSetup);

        if (user && Session.get('role') !== user.role) {
          Session.set('role', user.role);
        }

        if (user.role == RoleEnum.DISPO) {
          this.featurePermissions =
            user.dispoPermissions.dispoAccess?.featurePermissions;
        }

        this.isLoadingRole$.next(false);
      });
    });

    if (window?.location?.href) {
      this.setActiveLinkByUrl(window.location.href);
    }
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed$),
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          let url = event.urlAfterRedirects;
          this.setActiveLinkByUrl(url);
        }
      });
  }

  setActiveLinkByUrl(url: string) {
    if (url.includes('/jobs')) {
      if (url.includes('/jobs/')) {
        this.activeRoute$.next('jobs/job');
      } else {
        this.activeRoute$.next('jobs');
      }
    }

    if (url.includes('/multiday-job')) {
      this.activeRoute$.next('jobs/job');
    }

    if (url.includes('/temp-work')) {
      if (url.includes('/temp-work/employments')) {
        if (url.includes('/temp-work/employments/')) {
          this.activeRoute$.next('temp-work/employments/employment');
        } else {
          this.activeRoute$.next('temp-work/employments');
        }
      } else if (url.includes('/temp-work/pooler-employments')) {
        this.activeRoute$.next('temp-work/pooler-employments');
      } else {
        this.activeRoute$.next('temp-work');
      }
    }
    if (url.includes('/job-requests')) {
      this.activeRoute$.next('job-requests');
    }
    if (url.includes('/care-management')) {
      this.activeRoute$.next('care-management');
    }
    if (url.includes('/customer/users')) {
      this.activeRoute$.next('users');
    }
    if (url.includes('/reports') && !url.includes('/admin/reports')) {
      this.activeRoute$.next('reports');
    }
    if (url.includes('/transcribe-job-list')) {
      this.activeRoute$.next('transcribe-job-list');
    }
    if (url.includes('/profile')) {
      if (url.includes('?userId')) {
        this.activeRoute$.next('users/user');
      } else {
        this.activeRoute$.next('profile');
      }
    }
    if (url.includes('/super-admin')) {
      this.superAdminNavigation$.next(true);
      if (url.includes('/super-admin/settings')) {
        this.activeRoute$.next('super-admin/settings');
      } else if (url.includes('/super-admin/demo-settings')) {
        this.activeRoute$.next('super-admin/demo-settings');
      } else {
        this.activeRoute$.next('super-admin');
      }
    } else if (this.superAdminNavigation$.value) {
      this.superAdminNavigation$.next(false);
    }
    if (url.includes('/admin') && !url.includes('/super-admin')) {
      this.adminNavigation$.next(true);
      if (url.includes('/admin/tariffs')) {
        this.activeRoute$.next('admin/tariffs');
      } else if (url.includes('/admin/job-defaults')) {
        this.activeRoute$.next('admin/job-defaults');
      } else if (url.includes('/admin/pooler-types')) {
        this.activeRoute$.next('admin/pooler-types');
      } else if (url.includes('/admin/demand-reasons')) {
        this.activeRoute$.next('admin/demand-reasons');
      } else if (url.includes('/admin/conditional-transfers')) {
        this.activeRoute$.next('admin/conditional-transfers');
      } else if (url.includes('/admin/agency-priorities')) {
        this.activeRoute$.next('admin/agency-priorities');
      } else if (url.includes('/admin/shared-pool-source-settings')) {
        this.activeRoute$.next('admin/shared-pool-source-settings');
      } else if (url.includes('/admin/customer-flags')) {
        this.activeRoute$.next('admin/customer-flags');
      } else if (url.includes('/admin/cancel-reasons')) {
        this.activeRoute$.next('admin/cancel-reasons');
      } else if (url.includes('/admin/certificate-groups')) {
        this.activeRoute$.next('admin/certificate-groups');
      } else if (url.includes('/admin/custom-criteria')) {
        this.activeRoute$.next('admin/custom-criteria');
      } else if (url.includes('/admin/perigon-mapping')) {
        this.activeRoute$.next('admin/perigon-mapping');
      } else if (url.includes('/admin/organisations')) {
        this.activeRoute$.next('admin/organisations');
      } else if (url.includes('/admin/reports')) {
        this.activeRoute$.next('admin/reports');
      } else {
        this.activeRoute$.next('admin');
      }
    } else if (this.adminNavigation$.value) {
      this.adminNavigation$.next(false);
    }
    if (url.includes('/availability')) {
      if (
        !url.includes('?userId') &&
        !url.includes('temp-work') &&
        !url.includes('availability-management')
      ) {
        this.activeRoute$.next('availability');
      } else if (
        url.includes('availability-management') &&
        !url.includes('temp-work')
      ) {
        this.activeRoute$.next('availability-management');
      } else if (url.includes('temp-work/availability-management')) {
        this.activeRoute$.next('temp-work/availability-management');
      } else if (
        !url.includes('?userId') &&
        url.includes('/temp-work/availability')
      ) {
        this.activeRoute$.next('temp-work/availability');
      } else if (url.includes('/temp-work/availability')) {
        this.activeRoute$.next('users/temp-work/availability');
      } else {
        this.activeRoute$.next('users/availability');
      }
    }
    if (url.includes('/support')) {
      this.activeRoute$.next('support');
    }
    if (url.includes('/dashboard')) {
      this.activeRoute$.next('dashboard');
    }
    if (url.includes('/planning-matrix')) {
      this.activeRoute$.next('planning-matrix');
    }
    if (url.includes('/job-matrix')) {
      this.activeRoute$.next('job-matrix');
    }
  }

  logout() {
    //@ts-ignore
    window.dataLayer.push({
      role: 'undefined',
    });
    this.sidenavService.close();

    this.ngZone.run(() => {
      this.router.navigate(['/logout']).then();
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.userTracker) {
      this.userTracker.stop();
    }
  }

  public onItemClick(event?: Event, browserBack = false): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (browserBack) {
      this.location.back();
    }
    this.sidenavService.close();
  }

  historyBack() {
    this.location.back();
  }
}
